// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: corner assistant settings on the client (Settings → Assistant → Suggestions).
// Loads and saves `/konvo/corner/settings` (PUT merges, so a change sends only itself) and
// the recent suggestions list. Starting points read `startersEnabled` / `starterLabels`.

import EngagementAccess from '@app/features/konvo_engagement/state/EngagementAccess';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	type CornerKind,
	type CornerRecent,
	type CornerSettings,
	CornerSettingsSchema,
} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('CornerSettings');

export const CORNER_SETTINGS_ENDPOINT = '/konvo/corner/settings';
export const CORNER_RECENT_ENDPOINT = '/konvo/corner/recent';
/** Messages from Konvo (nudges in the Konvo DM) on or off: `{enabled}`. */
export const NUDGE_SETTINGS_ENDPOINT = '/konvo/assistant/nudges/settings';

/** Settings from the server with every missing field at its default; null when unusable. */
export function readCornerSettings(body: unknown): CornerSettings | null {
	if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
	const parsed = CornerSettingsSchema.safeParse(body);
	return parsed.success ? parsed.data : null;
}

/** A partial change; `kinds` merges per kind. */
export type CornerSettingsPatch = Partial<Omit<CornerSettings, 'kinds'>> & {kinds?: Partial<CornerSettings['kinds']>};

/** Applies a patch the way the server merges it (kinds per key, paused_until replaced). */
export function mergeCornerSettings(current: CornerSettings, patch: CornerSettingsPatch): CornerSettings {
	return {...current, ...patch, kinds: {...current.kinds, ...(patch.kinds ?? {})}};
}

/** What the screen shows: the server's settings with the saves still on their way laid on top. */
export function displayedCornerSettings(
	confirmed: CornerSettings | null,
	pending: ReadonlyArray<CornerSettingsPatch>,
): CornerSettings | null {
	if (!confirmed) return null;
	return pending.reduce<CornerSettings>((current, patch) => mergeCornerSettings(current, patch), confirmed);
}

export class CornerSettingsStore {
	settings: CornerSettings | null = null;
	recent: CornerRecent['items'] | null = null;
	/** Messages from Konvo in the Konvo DM; null until loaded. */
	nudgesEnabled: boolean | null = null;
	loading = false;
	saving = false;
	private loaded: Promise<void> | null = null;
	/** The last settings the server confirmed; `settings` adds the saves still in flight. */
	private confirmed: CornerSettings | null = null;
	private pending: Array<CornerSettingsPatch> = [];
	/** Saves go one at a time, in order, so a slow early reply never lands over a later change. */
	private saveChain: Promise<void> = Promise.resolve();
	/** Bumped on reset (sign-out, account switch): replies for the previous person are dropped. */
	private epoch = 0;
	/** The newest load; an older one's reply or failure no longer counts. */
	private loadToken = 0;
	/** Bumped when a save lands, so a load started before it does not bring back older values. */
	private savedVersion = 0;

	constructor() {
		makeAutoObservable<this, 'loaded' | 'confirmed' | 'pending' | 'saveChain' | 'epoch' | 'loadToken' | 'savedVersion'>(
			this,
			{
				loaded: false,
				confirmed: false,
				pending: false,
				saveChain: false,
				epoch: false,
				loadToken: false,
				savedVersion: false,
			},
			{autoBind: true},
		);
	}

	/** Starting points show: the new experience is on for this person (the rollout) and they kept them on. */
	get startersEnabled(): boolean {
		return EngagementAccess.enabled && (this.settings?.starters_enabled ?? true);
	}

	get starterLabels(): 'always' | 'icons_after_use' {
		return this.settings?.starter_labels ?? 'icons_after_use';
	}

	/** Pop-ups are on (default on until loaded). */
	get enabled(): boolean {
		return this.settings?.enabled ?? true;
	}

	/** "Only in the list": nothing pops up, everything goes into the From Konvo list. */
	get listOnly(): boolean {
		return this.settings?.popups === 'list_only';
	}

	kindEnabled(kind: CornerKind): boolean {
		return this.settings?.kinds[kind] ?? true;
	}

	/** When a kind paused itself after three ignores, the time it resumes. */
	pausedUntil(kind: CornerKind, now = Date.now()): Date | null {
		const iso = this.settings?.paused_until[kind];
		if (!iso) return null;
		const at = Date.parse(iso);
		return Number.isFinite(at) && at > now ? new Date(at) : null;
	}

	/** Forget everything about the signed-in person (sign-out, account switch). */
	reset(): void {
		this.epoch += 1;
		this.loadToken += 1;
		this.settings = null;
		this.confirmed = null;
		this.pending = [];
		this.saveChain = Promise.resolve();
		this.recent = null;
		this.nudgesEnabled = null;
		this.loading = false;
		this.saving = false;
		this.loaded = null;
	}

	private recompute(): void {
		this.settings = displayedCornerSettings(this.confirmed, this.pending);
	}

	/** Load once (later calls share the first request); `force` reloads. */
	load(force = false): Promise<void> {
		if (this.loaded && !force) return this.loaded;
		const token = ++this.loadToken;
		const version = this.savedVersion;
		const current = () => token === this.loadToken;
		this.loading = true;
		const request = http
			.get<CornerSettings>(CORNER_SETTINGS_ENDPOINT)
			.then((response) => {
				const settings = readCornerSettings(response.body);
				runInAction(() => {
					// A save that landed meanwhile is newer than this reply.
					if (!current() || !settings || version !== this.savedVersion) return;
					this.confirmed = settings;
					this.recompute();
				});
			})
			.catch((error) => {
				logger.warn('Could not load corner settings', error);
				runInAction(() => {
					if (current()) this.loaded = null;
				});
			})
			.finally(() =>
				runInAction(() => {
					if (current()) this.loading = false;
				}),
			);
		this.loaded = request;
		return request;
	}

	async loadRecent(): Promise<void> {
		const epoch = this.epoch;
		try {
			// kinds=all: this app knows the kinds added after the first apps (agent_task).
			const response = await http.get<CornerRecent>(CORNER_RECENT_ENDPOINT, {query: {kinds: 'all'}});
			const items = response.body?.items;
			runInAction(() => {
				if (epoch !== this.epoch) return;
				this.recent = Array.isArray(items) ? items : [];
			});
		} catch (error) {
			logger.warn('Could not load recent suggestions', error);
			runInAction(() => {
				if (epoch !== this.epoch) return;
				this.recent = this.recent ?? [];
			});
		}
	}

	async loadNudges(): Promise<void> {
		const epoch = this.epoch;
		try {
			const response = await http.get<{enabled?: unknown}>(NUDGE_SETTINGS_ENDPOINT);
			const enabled = response.body?.enabled;
			runInAction(() => {
				if (epoch !== this.epoch) return;
				this.nudgesEnabled = typeof enabled === 'boolean' ? enabled : true;
			});
		} catch (error) {
			logger.warn('Could not load nudge settings', error);
		}
	}

	async saveNudges(enabled: boolean): Promise<void> {
		const epoch = this.epoch;
		const before = this.nudgesEnabled;
		this.nudgesEnabled = enabled;
		try {
			await http.put(NUDGE_SETTINGS_ENDPOINT, {body: {enabled}});
		} catch (error) {
			runInAction(() => {
				// Only undo our own change: a later toggle (or another person) owns the value now.
				if (epoch === this.epoch && this.nudgesEnabled === enabled) this.nudgesEnabled = before;
			});
			throw error;
		}
	}

	/**
	 * Save a change: shown at once, sent after any earlier save finished, and taken back alone if
	 * the server refuses (changes made meanwhile stay).
	 */
	save(patch: CornerSettingsPatch): Promise<void> {
		const epoch = this.epoch;
		this.pending.push(patch);
		this.recompute();
		this.saving = true;
		const run = async () => {
			if (epoch !== this.epoch) return;
			try {
				const response = await http.put<CornerSettings>(CORNER_SETTINGS_ENDPOINT, {body: patch});
				const settings = readCornerSettings(response.body);
				runInAction(() => {
					if (epoch !== this.epoch) return;
					this.pending = this.pending.filter((p) => p !== patch);
					this.confirmed = settings ?? (this.confirmed ? mergeCornerSettings(this.confirmed, patch) : null);
					this.savedVersion += 1;
					this.recompute();
				});
			} catch (error) {
				runInAction(() => {
					if (epoch !== this.epoch) return;
					this.pending = this.pending.filter((p) => p !== patch);
					this.recompute();
				});
				throw error;
			} finally {
				runInAction(() => {
					if (epoch === this.epoch && this.pending.length === 0) this.saving = false;
				});
			}
		};
		const result = this.saveChain.then(run);
		this.saveChain = result.catch(() => undefined);
		return result;
	}

	/** The server changed a setting itself (Stop this kind from a pop-up): show it now. */
	applyServerChange(patch: CornerSettingsPatch): void {
		if (!this.confirmed) return;
		this.confirmed = mergeCornerSettings(this.confirmed, patch);
		this.recompute();
	}

	/**
	 * The server answered with the whole settings (an answer to "Am I getting in your way?", or its
	 * Undo): they replace what was confirmed, fields it no longer has included.
	 */
	replaceFromServer(body: unknown): void {
		const settings = readCornerSettings(body);
		if (!settings) return;
		this.confirmed = settings;
		this.savedVersion += 1;
		this.recompute();
	}

	/** The server changed something itself (Stop this kind from a pop-up): reload quietly. */
	refresh(): void {
		void this.load(true);
	}
}

export default new CornerSettingsStore();
